import { FormattedMessage, FormattedNumber } from "react-intl";

import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";

import {
  TAX_DETAILS_PERCENT_MAX_FRACTION_DIGITS,
  TAX_DETAILS_STATUS,
} from "../TaxDetails.consts";
import { TAX_DETAILS_MESSAGES } from "../TaxDetails.messages";
import type { TaxRegistration } from "../TaxDetails.types";
import { formatRegistrationTaxTypeName } from "../TaxDetails.utils";

type TaxDetailsBannerDescriptionProps = {
  registration: TaxRegistration;
};

/**
 * Renders the banner body copy for the registration's validation status.
 */
const TaxDetailsBannerDescription = ({
  registration,
}: TaxDetailsBannerDescriptionProps) => {
  const intl = useIntl();
  const { status, vatNumber, vatPercent } = registration;

  // Labels name the registration's tax — "HST ID", "QST %".
  const taxTypeName = formatRegistrationTaxTypeName({ intl, registration });

  if (status === TAX_DETAILS_STATUS.pending) {
    return (
      <Text typeface="body-1" className="tw-text-gray-600">
        <FormattedMessage
          id="tax_details.tax_details_banner_description.pending_status"
          defaultMessage="We're validating your VAT ID. You'll be notified once it's confirmed."
        />
      </Text>
    );
  }

  if (
    status === TAX_DETAILS_STATUS.validated ||
    status === TAX_DETAILS_STATUS.unsupported
  ) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Text typeface="body-1" className="tw-text-gray-600">
          <FormattedMessage
            {...TAX_DETAILS_MESSAGES.taxId}
            values={{ taxType: taxTypeName }}
          />
          : {vatNumber}
        </Text>
        {vatPercent !== undefined && (
          <Text typeface="body-1" className="tw-text-gray-600">
            <FormattedMessage
              {...TAX_DETAILS_MESSAGES.taxRate}
              values={{ taxType: taxTypeName }}
            />
            :{" "}
            <FormattedNumber
              value={vatPercent}
              style="percent"
              maximumFractionDigits={TAX_DETAILS_PERCENT_MAX_FRACTION_DIGITS}
            />
          </Text>
        )}
      </div>
    );
  }

  return null;
};

export { TaxDetailsBannerDescription };
